// src/components/Feed/MemberHome.js
// @flow

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import MemberHeader from './MemberHeader';
import MemberDrawer from '../MemberDrawer';
import MemberContent from './MemberContent';
import { log } from '../../utils/jsUtils';
import { debugTagsInRedux } from '../../utils/debugUtils';
import memberHomeStyles from './Styles/MemberHome.Style';
import { selectIsDemoMode } from '../../reduxStore/modules/common/selectors';
import { selectUser } from '../../reduxStore/modules/user/selectors';
import store from '../../reduxStore';
import { useGetFeedQuery, useGetFilterOptionsQuery } from '../../features/api/feedApi';
import { fetchTags } from '../../reduxStore/modules/tags/actions';
import { getFeedPollingInterval } from '../../utils/feedPollingUtils';
import {
  selectFilterSelections,
  selectFilterSearchText,
  setRequests,
  setUnits,
  selectRequests
} from '../../reduxStore/modules/feed';
import { setIsAppLoading } from '../../reduxStore/modules/common';
import { setSnackMessage } from '../../reduxStore/modules/common/actions';
import { selectAllTags } from '../../reduxStore/modules/tags/selectors';
import SPBackend from '../../services/SPBackend';

// Array equality check for merging requests
const requestsEqual = (arr1, arr2) => {
  if (!arr1 || !arr2 || arr1.length !== arr2.length) return false;

  // Check if the requests are the same, including content that affects UI display
  return arr1.every((req1, i) => {
    const req2 = arr2[i];
    return req1._id === req2._id &&
           req1.lookupTenant?._id === req2.lookupTenant?._id &&
           req1.Issue === req2.Issue && // Check message content
           req1.UnreadCount === req2.UnreadCount && // Check unread status
           req1.Status === req2.Status && // Check status changes
           req1.UpdatedDt === req2.UpdatedDt && // Check timestamp changes
           JSON.stringify(req1.lookupTenant?.Tags) === JSON.stringify(req2.lookupTenant?.Tags);
  });
};

const MemberHome = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [lastRefreshDtt, setLastRefreshDtt] = useState('');
  const isFetchingTags = useRef(false);
  const hasInitiallyFetchedTags = useRef(false);
  const prevRequestsRef = useRef([]);
  
  // Get user from combined auth + profile selector
  const user = useSelector(selectUser);
  
  const filterSelections = useSelector(selectFilterSelections);
  const filterSearchText = useSelector(selectFilterSearchText);
  const isDemoMode = useSelector(selectIsDemoMode);
  const allTags = useSelector(selectAllTags);
  const existingRequests = useSelector(selectRequests);

  // Extract stable primitive from user object to prevent RTK Query refetches on user profile updates
  const userUid = user?.uid;

  // Drawer toggle with useCallback
  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  // RTK Query hooks for feed data - with stable parameters using primitive userId instead of user object
  const feedQueryParams = useMemo(() => ({
    filters: {
      ...filterSelections,
      startDatetime: lastRefreshDtt || '',
      endDatetime: ''
    },
    searchText: filterSearchText,
    userId: userUid
  }), [filterSelections, lastRefreshDtt, filterSearchText, userUid]);
  
  const {
    data: feedData,
    isFetching: isFeedFetching,
    isLoading: isFeedLoading,
    isError,
    error,
    refetch
  } = useGetFeedQuery(feedQueryParams, {
    skip: !userUid,
    refetchOnMountOrArgChange: true,
    pollingInterval: getFeedPollingInterval(), // Environment-based polling (10s production, 30s development)
    refetchOnFocus: true,   // Refetch when tab becomes active
    refetchOnReconnect: true // Refetch on network reconnection
  });

  // Sync RTK Query loading state to Redux isAppLoading for horizontal spinner
  // Only show spinner on initial load, not during polling/refetches
  useEffect(() => {
    dispatch(setIsAppLoading(isFeedLoading));
  }, [isFeedLoading, dispatch]);

  // Use RTK Query for filter options
  const { data: filterOptions } = useGetFilterOptionsQuery(
    { userId: userUid },
    { skip: !userUid }
  );
  
  // Note: Legacy fetchFeed removed - RTK Query (useGetFeedQuery) is now the single source of truth for feed data

  // Monitor feed data from RTK Query
  useEffect(() => {
    if (feedData) {
      window.console.log(`[MemberHome] RTK Query returned ${feedData.requests?.length || 0} items:`, {
        hasRequests: !!feedData.requests?.length,
        itemsCount: feedData.requests?.length || 0,
        timestamp: new Date().toISOString()
      });
      
      // Create a success notification if we have data
      if (feedData.requests?.length > 0) {
        window.console.log('%c[MemberHome] FEED LOADED SUCCESSFULLY! 🎉', 
          'background: #4caf50; color: white; font-size: 14px', {
            count: feedData.requests.length,
            timestamp: new Date().toISOString()
          });
      }
    } else if (isError) {
      window.console.error('[MemberHome] Feed fetch error:', error, 
        'RTK Query will automatically retry based on polling configuration.');
    }
  }, [feedData, isError, error]);

  // Process feed data after it's received
  useEffect(() => {
    if (feedData?.requests) {
      log('[MemberHome] Received feed data from RTK Query', { 
        count: feedData.requests.length,
        timestamp: new Date().toISOString()
      });
      
      // Merge existing requests with new data while preserving only tag data
      const mergedRequests = feedData.requests.map(newReq => {
        // Find matching existing request to preserve tag data
        const existingReq = existingRequests.find(r => r._id === newReq._id);

        if (existingReq && existingReq.lookupTenant && newReq.lookupTenant) {
          // Only preserve Tags from existing data, use fresh data for everything else
          // This ensures Issue, UnreadCount, UpdatedDt, etc. are always fresh from RTK
          return {
            ...newReq, // Use all fresh data from RTK
            lookupTenant: {
              ...newReq.lookupTenant, // Use fresh lookupTenant data
              Tags: existingReq.lookupTenant.Tags || newReq.lookupTenant.Tags || [] // Only preserve Tags
            }
          };
        }

        return newReq;
      });
      
      // Only update Redux if the requests have changed to prevent loops
      if (!requestsEqual(mergedRequests, prevRequestsRef.current)) {
        log('[MemberHome] Updating Redux store with merged requests', {
          count: mergedRequests.length,
          totalCount: feedData.totalRequests || mergedRequests.length
        });
        
        dispatch(setRequests(mergedRequests, feedData.totalRequests || mergedRequests.length));
        dispatch(setUnits(feedData.units || []));
        
        // Store reference for comparison
        prevRequestsRef.current = mergedRequests;
      }
    }
  }, [feedData, dispatch, existingRequests]);

  // Enhanced tag fetching with stable references
  useEffect(() => {
    // Only fetch tags if we have a valid user and haven't already fetched
    if (user?.uid && !isFetchingTags.current && !hasInitiallyFetchedTags.current) {
      isFetchingTags.current = true;

      dispatch(fetchTags(user))
        .then((action) => {
          if (action?.payload) {
            // Tags are now stored in state.tags.tags (single source of truth)
            // Run tag debugging only in development
            if (process.env.NODE_ENV !== 'production') {
              debugTagsInRedux(store.getState());
            }
          }
          isFetchingTags.current = false;
          hasInitiallyFetchedTags.current = true;
        })
        .catch(err => {
          log('[MemberHome] Error fetching tags:', err);
          isFetchingTags.current = false;
        });
    }
  }, [user, dispatch]);

  // Example functions - wrapped with useCallback
  const sendBroadcast = useCallback((toTenantsIds, messagePayload, successCallback, failureCallback) => {
    log('[MemberHome][sendBroadcast] toTenantsIds, messagePayload: ', toTenantsIds, messagePayload);
    SPBackend.sendPMToTenants(toTenantsIds, messagePayload, user)
      .then((response) => {
        const { data } = response;
        log('[MemberHome][sendBroadcast] response: ', response);
        dispatch(setSnackMessage({
          message: data?.message || 'Broadcast sent successfully',
          level: data?.success ? 'success' : 'error',
          duration: 3000
        }));
        successCallback();
      })
      .catch((error) => {
        log('[MemberHome][sendBroadcast] error: ', error);
        const errorMsg = error?.response?.data?.message || error?.message || 'Failed to send broadcast';
        dispatch(setSnackMessage({
          message: errorMsg,
          level: 'error',
          duration: 3000
        }));
        failureCallback(errorMsg);
      });
  }, [user, dispatch]);

  const exportCSV = useCallback(() => {
    // ...
  }, []);

  // Memoize child components to prevent unnecessary re-renders
  const memberHeader = useMemo(() => (
    <MemberHeader
      mobileOpen={mobileOpen}
      usehandleDrawerToggle={handleDrawerToggle}
      sendBroadcast={sendBroadcast}
      isDemoMode={isDemoMode}
    />
  ), [mobileOpen, handleDrawerToggle, sendBroadcast, isDemoMode]);

  const memberDrawer = useMemo(() => (
    <MemberDrawer
      mobileOpen={mobileOpen}
      usehandleDrawerToggle={handleDrawerToggle}
    />
  ), [mobileOpen, handleDrawerToggle]);

  const memberContent = useMemo(() => (
    <MemberContent 
      exportCSV={exportCSV}
      feedData={feedData}
      isFeedFetching={isFeedFetching}
    />
  ), [exportCSV, feedData, isFeedFetching]);

  return (
    <Box sx={memberHomeStyles.root(theme)}>
      <Helmet>
        <title>Neo | Feed</title>
      </Helmet>

      {memberHeader}
      {memberDrawer}
      {memberContent}
    </Box>
  );
};

export default React.memo(MemberHome);